import React, { Component } from 'react';
import ElementInfoAdapter
    from '@src/slide/application/service/edit-action-services/element-info';
import Cropable from '../../../../cropable';
import GraphicContent from '../../../../graphic-content';
import { advHas, omitStyle } from '@src/slide/infrastructure/module/independent/assert/get-set';
import omit from 'lodash/omit';
import { observer } from 'mobx-react';
import _has from 'lodash/has';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { IFrame } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';

const DEFAULT_SRC_RECT = { l: 0, r: 0, b: 0, t: 0 };

interface Props {
    element: IShapeModel;
    scale: number;
    rect?: IFrame;
    rotate?: number;
    relativeRotate?: number;
    relativeFlipH?: number;
    relativeFlipV?: number;
}

@observer
class ShapeCropping extends Component<Props> {
    private getCropSize(rectInfo: any): any {
        const { width } = rectInfo;
        const { height } = rectInfo;
        const { left } = rectInfo;
        const { top } = rectInfo;
        const componentProps = this.props.element.props;
        // eslint-disable-next-line no-nested-ternary
        let srcRect = (rectInfo?.srcRect)
            ? rectInfo.srcRect
            : componentProps.style.fillBackground?.image?.stretch
                ? componentProps.style.fillBackground.image.stretch.fillRect : null;
        srcRect = this.resetSrcRect(srcRect);

        const pl = (width * srcRect.l);
        const pr = (width * srcRect.r);

        const pt = (height * srcRect.t);
        const pb = (height * srcRect.b);

        const rw = width + pl + pr;
        const rh = height + pt + pb;

        return {
            width: rw,
            height: rh,
            left: left - pl,
            top: top - pt,
            aLeft: pl,
            aTop: pt,
            aRight: pr,
            aBottom: pb,
            centerX: (width / 2) + left,
            centerY: (height / 2) + top,
        };
    }

    private getOldSrcRect(rectInfo) {
        const componentProps = this.props.element.props;
        let srcRect = rectInfo?.srcRect
            ?? componentProps.style.fillBackground?.image?.stretch?.fillRect;
        srcRect = this.resetSrcRect(srcRect);
        return srcRect;
    }

    private getRectInfo(elementModel, elementProps) {
        const rectInfo: any = { ...this.props.rect };
        if (_has(elementModel, 'srcRect.t')) {
            rectInfo.srcRect = elementModel.srcRect;
        } else if (advHas(elementProps.style.fillBackground, 'image.stretch')) {
            const { fillRect } = elementProps.style.fillBackground.image.stretch;
            rectInfo.srcRect = fillRect ? this.resetSrcRect(fillRect) : fillRect;
        }
        return rectInfo;
    }

    private resetSrcRect(srcRectParam) {
        return {
            ...DEFAULT_SRC_RECT,
            ...srcRectParam,
        };
    }

    private getSizeFromProps(elementProps) {
        return {
            width: this.props.rect ? this.props.rect.width : elementProps.style.width,
            height: this.props.rect ? this.props.rect.height : elementProps.style.height,
            left: this.props.rect ? this.props.rect.left : elementProps.style.left,
            top: this.props.rect ? this.props.rect.top : elementProps.style.top,
        };
    }
    /**
     * 生成渲染需要的参数
     * @param elementModel
     * @param elementProps
     * @param size
     * @returns {{pr: string, pb: string, rectInfo, pt: string, src: string, rw: number, rh: number, pl: string}}
     */
    private getRenderParams(elementModel, elementProps, size) {
        let src = '';
        let pl: number | string = 0;
        let pr: number | string = 0;
        let pt: number | string = 0;
        let pb: number | string = 0;
        let rw = 0;
        let rh = 0;
        const rectInfo = this.getRectInfo(elementModel, elementProps);
        if (advHas(elementProps.style.fillBackground, 'image')) {
            const stretchParam = elementProps.style.fillBackground.image.stretch;
            const fillRect = stretchParam?.fillRect ?? null;
            let srcRect = rectInfo?.srcRect ?? fillRect;
            srcRect = this.resetSrcRect(srcRect);

            src = elementProps.style.fillBackground.image.src;
            pl = (size.width * srcRect.l);
            pr = (size.width * srcRect.r);

            pt = (size.height * srcRect.t);
            pb = (size.height * srcRect.b);

            rw = size.width + pl + pr;
            rh = size.height + pt + pb;

            if (isNaN(rw)) rw = 0;
            if (isNaN(rh)) rh = 0;

            pl = `${-pl}px`;
            pr = `${-pr}px`;
            pt = `${-pt}px`;
            pb = `${-pb}px`;
        } else {
            pl = `${pl}px`;
            pr = `${pr}px`;
            pt = `${pt}px`;
            pb = `${pb}px`;
        }
        return { src, pl, pr, pt, pb, rw, rh, rectInfo };
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public render() {
        const elementModel = this.props.element;
        const elementProps = this.props.element.props;
        const size = this.getSizeFromProps(elementProps);
        const propsRect = this.props.rect;
        const flip = {
            flipH: propsRect ? propsRect.flipH : 0,
            flipV: propsRect ? propsRect.flipV : 0,
        };
        const { src, pl, pr, pt, pb, rw, rh, rectInfo } = this.getRenderParams(elementModel, elementProps, size);
        return (
            <Cropable
                component={this.props.element}
                rw={rw}
                rh={rh}
                pl={pl}
                pt={pt}
                pr={pr}
                pb={pb}
                src={src}
                flip={flip}
                width={size.width}
                height={size.height}
                scale={this.props.scale}
                rotate={this.props.rotate || 0}
                type={this.props.element.type}
                relativeRotate={this.props.relativeRotate || 0}
                relativeFlipH={this.props.relativeFlipH || 0}
                relativeFlipV={this.props.relativeFlipV || 0}
                getCropSize={this.getCropSize.bind(this, rectInfo)}
                getSize={() => ElementInfoAdapter.sizeInfo(elementModel)}
                getFlip={() => ElementInfoAdapter.flipInfo(elementModel)}
                getOldSrcRect={this.getOldSrcRect.bind(this, rectInfo)}
                isCroping={true}
            >
                <GraphicContent
                    model={{
                        ...omit(omitStyle(elementProps.style), 'width', 'height', 'left', 'top'),
                        ...size,
                        rect: rectInfo,
                        rotate: this.props.rotate,
                        isFillRect: true,
                    }}
                    scale={this.props.scale}
                    relativeRotate={this.props.relativeRotate}
                    relativeFlipH={this.props.relativeFlipH}
                    relativeFlipV={this.props.relativeFlipV}
                    pointerEvents={'none'}
                    mkey={elementModel.key}
                    style={{ position: 'absolute' }}
                />
            </Cropable>
        );
    }
}

export default ShapeCropping;
